/**
 * Key combos in the kit's notation (`'Mod+Shift+F'`) as the settings need to
 * compare and check them — pure, no UI kit: a settings declaration is read in
 * node-env code, too.
 *
 * `Mod` is ⌘ on an Apple platform and Ctrl elsewhere, and a setting is shared
 * across the devices of one person. So two combos count as the same when they
 * are equal on **either** platform ({@link connectComboIdentities}), and the
 * global search is refused on every platform ({@link connectComboIsGlobalSearch}).
 */

type Modifier = "ctrl" | "meta" | "alt" | "shift";

const MODIFIER_NAMES: Record<string, Modifier | "mod"> = {
  mod: "mod",
  ctrl: "ctrl",
  control: "ctrl",
  strg: "ctrl",
  meta: "meta",
  cmd: "meta",
  command: "meta",
  win: "meta",
  alt: "alt",
  option: "alt",
  opt: "alt",
  shift: "shift",
};

/** The spellings the kit's hotkeys fold onto one key name. */
const KEY_ALIASES: Record<string, string> = {
  esc: "escape",
  return: "enter",
  del: "delete",
  space: " ",
  spacebar: " ",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
  plus: "+",
};

interface ParsedCombo {
  /** Modifiers other than `Mod`. */
  mods: ReadonlySet<Modifier>;
  mod: boolean;
  key: string;
}

/** Null: empty or only modifiers. */
function parse(combo: unknown): ParsedCombo | null {
  if (typeof combo !== "string") return null;
  // „Mod++“ is the plus key; splitting at every „+“ would lose it.
  const plus = /.\s*\+\s*\+\s*$/.test(combo);
  const parts = (plus ? combo.replace(/\s*\+\s*\+\s*$/, "") : combo)
    .split(/\s*\+\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (plus) parts.push("+");
  const mods = new Set<Modifier>();
  let mod = false;
  let key = "";
  for (const part of parts) {
    const id = part.toLowerCase();
    const named = MODIFIER_NAMES[id];
    if (named === "mod") mod = true;
    else if (named) mods.add(named);
    else key = KEY_ALIASES[id] ?? id;
  }
  return key ? { mods, mod, key } : null;
}

/**
 * The identities of a combo: its modifier set and lower-case key with `Mod`
 * resolved once as ⌘ (meta) and once as Ctrl, aliases folded — `Mod+Shift+F`
 * is `meta+shift|f` and `ctrl+shift|f`, `Strg+Shift+f` is `ctrl+shift|f`. Two
 * combos are the same when they share an identity. Empty for no combo.
 */
export function connectComboIdentities(combo: unknown): string[] {
  const parsed = parse(combo);
  if (!parsed) return [];
  const resolved = parsed.mod
    ? (["meta", "ctrl"] as const).map(
        (command) => new Set<Modifier>([...parsed.mods, command]),
      )
    : [parsed.mods];
  return [
    ...new Set(
      resolved.map((mods) => `${[...mods].sort().join("+")}|${parsed.key}`),
    ),
  ];
}

/**
 * Whether the combo is K with Ctrl, Meta or Mod — whatever else is held. The
 * shell opens the global search on ⌘ or Ctrl + K on every platform (⌃K on a
 * Mac too), so no setting may take it.
 */
export function connectComboIsGlobalSearch(combo: unknown): boolean {
  const parsed = parse(combo);
  if (!parsed || parsed.key !== "k") return false;
  return parsed.mod || parsed.mods.has("ctrl") || parsed.mods.has("meta");
}
